// src/features/rrhh/mensajeria/components/ReclamoDetailModal.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const ESTADO_OPTIONS = [
  { label: "Nuevo", value: "NUEVO" },
  { label: "En revisión", value: "EN_REVISION" },
  { label: "Respondido", value: "RESPONDIDO" },
  { label: "Cerrado", value: "CERRADO" },
];

function TitlePill({ children, sx }) {
  return (
    <Box
      sx={{
        backgroundColor: "rgba(242, 243, 249, 1)",
        borderRadius: "33px",
        padding: "10px 20px",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        ...sx,
      }}
    >
      <Typography sx={{ fontSize: 16, fontWeight: 600, color: "rgba(2, 8, 23, 1)" }}>
        {children}
      </Typography>
    </Box>
  );
}

function ValueCapsule({ children, sx }) {
  return (
    <Box
      sx={{
        backgroundColor: "rgba(247, 247, 248, 1)",
        borderRadius: "20px",
        padding: "10px",
        boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
        display: "inline-flex",
        alignItems: "center",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export default function ReclamoDetailModal({
  open,
  mode = "detalle",
  reclamo,
  onClose,
  onSubmitEstado,
  savingEstado = false,
  onSubmitRespuesta,
  savingRespuesta = false, 
}) {
  const title = useMemo(() => {
    if (mode === "estado") return "Cambiar Estado";
    if (mode === "responder") return "Responder reclamo";
    if (mode === "full") return "Vista planilla completa";
    return "Ver detalle del reclamo";
  }, [mode]);

  const [nuevoEstado, setNuevoEstado] = useState("");
  const [respuesta, setRespuesta] = useState("");

  useEffect(() => {
    if (!open) return;

    if (mode === "estado") {
      setNuevoEstado("");
      setRespuesta("");
      return;
    }

    // otros modos
    setNuevoEstado(reclamo?.estado || "");
    setRespuesta("");
  }, [open, reclamo?.id, mode, reclamo?.estado]);

  const disabled = !reclamo && mode !== "full";

  // ✅ modal chico para "estado"
  const modalWidth = mode === "estado" ? 380 : 530;

  // ✅ en Figma "estado" no necesita X (tenés Cancelar)
  const showClose = mode !== "estado";

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          background: "transparent",
          boxShadow: "none",
          overflow: "visible",
        },
      }}
    >
      <Box
        sx={{
          width: { xs: "calc(100vw - 24px)", sm: `${modalWidth}px` },
          backgroundColor: "rgba(117, 117, 138, 1)",
          borderRadius: "30px",
          padding: "20px",
          boxShadow: "0px 24px 80px rgba(0,0,0,.35)",
          position: "relative",
        }}
      >
        {showClose ? (
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              width: 36,
              height: 36,
              bgcolor: "rgba(242, 243, 249, 1)",
              boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
              "&:hover": { bgcolor: "rgba(247, 247, 248, 1)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}

        <Stack spacing={2}>
          {/* Título */}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <TitlePill sx={mode === "estado" ? { py: 0.75, px: 2.5 } : null}>{title}</TitlePill>
          </Box>

          {mode === "full" ? (
            <ValueCapsule sx={{ width: "100%", height: 220, justifyContent: "center" }}>
              <Typography sx={{ opacity: 0.85 }}>
                Placeholder visual: acá después conectamos “Vista planilla completa”.
              </Typography>
            </ValueCapsule>
          ) : disabled ? (
            <ValueCapsule sx={{ width: "100%", justifyContent: "center" }}>
              <Typography sx={{ opacity: 0.75 }}>No hay reclamo seleccionado.</Typography>
            </ValueCapsule>
          ) : mode === "estado" ? (
            <>
              {/* ✅ Card superior info (compacta) */}
              <Stack
                spacing={0.5}
                sx={{
                  backgroundColor: "rgba(247, 247, 248, 1)",
                  borderRadius: "20px",
                  padding: 1.25,
                  boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "rgba(2, 8, 23, 1)" }}>
                  Reclamo: {reclamo.tipo} - {reclamo.titulo}
                </Typography>

                <Typography sx={{ fontSize: 12.5, color: "rgba(2, 8, 23, 1)" }}>
                  <b>Enviado el:</b> {reclamo.fecha}
                </Typography>

                <Typography sx={{ fontSize: 12.5, color: "rgba(2, 8, 23, 1)" }}>
                  <b>Por:</b> {reclamo.empleado} (Sucursal: {reclamo.sucursal})
                </Typography>
              </Stack>

              {/* ✅ Estado actual + Nuevo estado (como Figma) */}
              <Stack spacing={1.25}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "rgba(247, 247, 248, 1)" }}>
                  Estado Actual
                </Typography>

                <ValueCapsule sx={{ width: "100%", justifyContent: "flex-start" }}>
                  <Typography sx={{ fontSize: 13.5, color: "rgba(2, 8, 23, 1)" }}>
                    {reclamo.estado}
                  </Typography>
                </ValueCapsule>

                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "rgba(247, 247, 248, 1)", mt: 0.5 }}>
                  Nuevo Estado
                </Typography>

                <ValueCapsule sx={{ width: "100%", p: 0 }}>
                  <TextField
                    select
                    value={nuevoEstado}
                    onChange={(e) => setNuevoEstado(e.target.value)}
                    variant="standard"
                    fullWidth
                    SelectProps={{ displayEmpty: true }}
                    sx={{
                      px: 1,
                      py: 0.25,
                      "& .MuiInput-root:before": { borderBottom: "none" },
                      "& .MuiInput-root:after": { borderBottom: "none" },
                      "& .MuiInputBase-input": {
                        fontSize: 13.5,
                        color: "rgba(2, 8, 23, 1)",
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Cambiar estado
                    </MenuItem>
                    {ESTADO_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </ValueCapsule>
              </Stack>

              {/* ✅ Botones centrados (Guardar / Cancelar) */}
              <Stack direction="row" spacing={1} sx={{ justifyContent: "center", pt: 0.5 }}>
                <Button
                  onClick={() => onSubmitEstado?.(nuevoEstado)}
                  disabled={
                    !nuevoEstado ||
                    savingEstado ||
                    (reclamo?._raw?.estado && nuevoEstado === reclamo._raw.estado)
                  }
                  sx={{
                    borderRadius: 999,
                    bgcolor: "rgba(242, 243, 249, 1)",
                    boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
                    color: "rgba(2, 8, 23, 1)",
                    "&:hover": { bgcolor: "rgba(247, 247, 248, 1)" },
                    "&.Mui-disabled": { opacity: 0.55 },
                    px: 2.5,
                  }}
                >
                  {savingEstado ? "Guardando…" : "Guardar"}
                </Button>


                <Button
                  onClick={onClose}
                  sx={{
                    borderRadius: 999,
                    bgcolor: "rgba(242, 243, 249, 1)",
                    boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
                    color: "rgba(2, 8, 23, 1)",
                    "&:hover": { bgcolor: "rgba(247, 247, 248, 1)" },
                    px: 2.5,
                  }}
                >
                  Cancelar
                </Button>
              </Stack>
            </>
          ) : (
            <>
              {/* ✅ (lo que ya tenías para detalle / responder) */}
              {/* Card superior info */}
              <Stack
                spacing={0.75}
                sx={{
                  backgroundColor: "rgba(247, 247, 248, 1)",
                  borderRadius: "20px",
                  padding: 1.5,
                  boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
                }}
              >
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: "rgba(2, 8, 23, 1)" }}>
                  Reclamo: {reclamo.tipo} - {reclamo.titulo}
                </Typography>

                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, color: "rgba(2, 8, 23, 1)" }}>
                    Enviado el:
                  </Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 400, color: "rgba(2, 8, 23, 1)" }}>
                    {reclamo.fecha}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, color: "rgba(2, 8, 23, 1)" }}>
                    Por:
                  </Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 400, color: "rgba(2, 8, 23, 1)" }}>
                    {reclamo.empleado}
                  </Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 400, color: "rgba(2, 8, 23, 1)" }}>
                    (Sucursal: {reclamo.sucursal})
                  </Typography>
                </Box>
              </Stack>

              {/* Prioridad + Estado (detalle/responder) */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", gap: 1, flex: 1 }}>
                  <TitlePill sx={{ padding: 1.5 }}>Prioridad</TitlePill>
                  <ValueCapsule>
                    <Typography sx={{ fontSize: 16, fontWeight: 400, color: "rgba(2, 8, 23, 1)" }}>
                      {reclamo.prioridad}
                    </Typography>
                  </ValueCapsule>
                </Box>

                <Box sx={{ display: "flex", gap: 1, flex: 1 }}>
                  <TitlePill sx={{ padding: 1.2 }}>Estado Actual</TitlePill>
                  <ValueCapsule>
                    <Typography sx={{ fontSize: 16, fontWeight: 400, color: "rgba(2, 8, 23, 1)" }}>
                      {reclamo.estado}
                    </Typography>
                  </ValueCapsule>
                </Box>
              </Box>

              {/* Descripción / Respuesta */}
              <Stack spacing={1} sx={{ width: "100%" }}>
                <TitlePill sx={{ padding: 1.5, height: "38px", width: "50%" }}>
                  {mode === "responder" ? "Respuesta:" : "Descripcion del empleado:"}
                </TitlePill>

                <ValueCapsule
                  sx={{
                    width: "100%",
                    maxHeight: "10vh",
                    display: "block",
                    overflowY: "auto",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {mode === "responder" ? (
                    <TextField
                      value={respuesta}
                      onChange={(e) => setRespuesta(e.target.value)}
                      placeholder="Escribí la respuesta..."
                      variant="standard"
                      multiline
                      minRows={4}
                      sx={{
                        width: "100%",
                        "& .MuiInput-root:before": { borderBottom: "none" },
                        "& .MuiInput-root:after": { borderBottom: "none" },
                        "& textarea": { fontSize: 16, color: "rgba(2, 8, 23, 1)" },
                      }}
                    />
                  ) : (
                    <Typography sx={{ fontSize: 16, fontWeight: 400, color: "rgba(2, 8, 23, 1)" }}>
                      {reclamo.descripcion}
                    </Typography>
                  )}
                </ValueCapsule>
              </Stack>

              {/* Botones detalle/responder */}
              {mode === "responder" && (
                <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
                  <Button
                    onClick={onClose}
                    sx={{
                      borderRadius: 999,
                      bgcolor: "rgba(242, 243, 249, 1)",
                      boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
                      color: "rgba(2, 8, 23, 1)",
                      "&:hover": { bgcolor: "rgba(247, 247, 248, 1)" },
                    }}
                  >
                    Cerrar
                  </Button>
                  
                  <Button
                    onClick={() => onSubmitRespuesta?.(respuesta)}
                    disabled={!String(respuesta || "").trim() || savingRespuesta}
                    sx={{
                      borderRadius: 999,
                      bgcolor: (t) => t.palette?.secondary?.[500],
                      color: "common.white",
                      "&:hover": { bgcolor: (t) => t.palette?.secondary?.[600] },
                      "&.Mui-disabled": { opacity: 0.55 },
                    }}
                  >
                    {savingRespuesta ? "Guardando…" : "Guardar"}
                  </Button>
                </Stack>
              )}
            </>
          )}
        </Stack>
      </Box>
    </Dialog>
  );
}
